import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    EmailAuthProvider,
    getAuth,
    reauthenticateWithCredential,
    updatePassword,
} from 'firebase/auth';

import InputForm from '../InputForm/InputForm';
import Button from '../Buttons/Button';
import './PrivacySettings.scss';

const RULES = {
    minLength: 8,
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /\d/,
    specialChar: /[!@#$%^&*(),.?":{}|<>]/,
};

function PrivacySettings() {
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [loading, setLoading] = useState(false);

    const passwordChecks = useMemo(() => {
        const pw = passwords.newPassword;
        return {
            minLength: pw.length >= RULES.minLength,
            uppercase: RULES.uppercase.test(pw),
            lowercase: RULES.lowercase.test(pw),
            number: RULES.number.test(pw),
            specialChar: RULES.specialChar.test(pw),
        };
    }, [passwords.newPassword]);

    const passwordStrength = useMemo(() => {
        const passed = Object.values(passwordChecks).filter(Boolean).length;
        return (passed / Object.keys(passwordChecks).length) * 100;
    }, [passwordChecks]);

    const handleChange = (event) => {
        const { id, value } = event.target;
        setPasswords((prev) => ({ ...prev, [id]: value }));
    };

    const handlePasswordChange = async () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser || !currentUser.email) {
            toast.error('No authenticated user found.');
            return;
        }

        const { currentPassword, newPassword, confirmPassword } = passwords;

        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('All fields are required.');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }
        if (passwordStrength < 100) {
            toast.error('Please meet all password requirements.');
            return;
        }

        setLoading(true);
        try {
            const credential = EmailAuthProvider.credential(
                currentUser.email,
                currentPassword
            );
            await reauthenticateWithCredential(currentUser, credential);
            await updatePassword(currentUser, newPassword);
            toast.success('Password updated successfully.');
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            const code = error?.code;
            if (code === 'auth/wrong-password') {
                toast.error('Current password is incorrect.');
            } else if (code === 'auth/weak-password') {
                toast.error('Password is too weak.');
            } else {
                toast.error('Error updating password: ' + (error?.message || 'unknown'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className='privacy'>
            <div className='container-password'>
                <div className='change-password'>
                    <h1>Change password</h1>
                </div>

                <InputForm
                    id="currentPassword"
                    onChange={handleChange}
                    value={passwords.currentPassword}
                    label="Current password"
                    type="password"
                />

                <InputForm
                    id="newPassword"
                    onChange={handleChange}
                    value={passwords.newPassword}
                    label="New password"
                    type="password"
                />

                <div className='privacy__strength'>
                    <div
                        className='privacy__strength-bar'
                        style={{ width: `${passwordStrength}%` }}
                        data-strength={
                            passwordStrength === 100
                                ? 'strong'
                                : passwordStrength >= 60
                                ? 'medium'
                                : 'weak'
                        }
                    />
                </div>
                <ul className='privacy__hints'>
                    <li className={passwordChecks.minLength ? 'is-valid' : ''}>
                        Minimum {RULES.minLength} characters
                    </li>
                    <li className={passwordChecks.uppercase ? 'is-valid' : ''}>
                        At least 1 uppercase letter
                    </li>
                    <li className={passwordChecks.lowercase ? 'is-valid' : ''}>
                        At least 1 lowercase letter
                    </li>
                    <li className={passwordChecks.number ? 'is-valid' : ''}>
                        At least 1 number
                    </li>
                    <li className={passwordChecks.specialChar ? 'is-valid' : ''}>
                        At least 1 special character
                    </li>
                </ul>

                <InputForm
                    id="confirmPassword"
                    onChange={handleChange}
                    value={passwords.confirmPassword}
                    label="Confirm password"
                    type="password"
                />

                <span>
                    <Button
                        primaryBtn
                        onClick={handlePasswordChange}
                        disabled={loading || passwordStrength < 100}
                        label={loading ? 'Updating…' : 'Save'}
                    />
                </span>
            </div>
        </section>
    );
}

export default PrivacySettings;
